/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';

import { propTypes } from '../../../../util/types';
import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';

import {
  ExternalLink,
  NamedLink,
  Avatar,
  IconBell,
  NotificationBadge,
} from '../../../../components';

import { InlineAuthenticationForms } from '../../../CheckoutPage/InlineAuthenticationForms';

import css from './TopbarMobileMenu.module.css';

const CategoryLinkComponent = ({ categories, closeMenu, currentPage }) => {
  const { name, id } = categories;
  const slug = `?pub_categoryLevel1=${id}`;
  const location = useLocation();

  const handleClick = event => {
    event.preventDefault(); // Prevent default navigation
    if (closeMenu) {
      closeMenu(); // Call closeMenu
    }
    window.history.pushState({}, '', `/s${slug}`); // Update the URL
  };

  // Check if this category is currently active by checking URL parameters
  const searchParams = new URLSearchParams(location.search);
  const currentCategoryId = searchParams.get('pub_categoryLevel1');
  const isCurrentCategory =
    currentPage === 'SearchPage' && currentCategoryId === String(id);

  return (
    <div
      onClick={handleClick}
      className={classNames(css.navigationLink, {
        [css.currentPage]: isCurrentCategory,
      })}
    >
      <NamedLink name={`SearchPage`} to={{ search: slug }}>
        {name}
      </NamedLink>
    </div>
  );
};

const CustomLinkComponent = ({ linkConfig, currentPage, closeMenu }) => {
  const { text, type, href, route } = linkConfig;
  const location = useLocation();

  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId =>
      hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab =>
      hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    // Special handling for SearchPage - only show as current if no category is selected
    if (page === 'SearchPage') {
      const searchParams = new URLSearchParams(location.search);
      const currentCategoryId = searchParams.get('pub_categoryLevel1');
      return isCurrentPage && !currentCategoryId ? css.currentPage : null;
    }

    return isCMSPage(route?.params?.pageId) ||
      isInboxPage(route?.params?.tab) ||
      isCurrentPage
      ? css.currentPage
      : null;
  };

  const handleClick = event => {
    if (type === 'internal' && route) {
      event.preventDefault(); // Prevent default navigation
      if (closeMenu) closeMenu(); // Close the menu
      const { name, params, to } = route || {};
      window.history.pushState({}, '', `/s${to.search}`); // Programmatically update the URL
    } else if (closeMenu) {
      closeMenu(); // Close the menu for external links
    }
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <div onClick={handleClick} className={className}>
        <NamedLink name={name} params={params} to={to}>
          <span className={css.menuItemBorder} />
          {text}
        </NamedLink>
      </div>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

const TopbarMobileMenu = props => {
  const {
    currentPage,
    categories,
    customLinks,
    isOpen,
    closeMenu,
    isAuthenticated,
    currentUser,
    onLogout,
    notificationCount,
    listingsCount,
    favoritesCount,
    savedSearchesCount,
    onLogin,
    onSignup,
    loginError,
    signupError,
    authInProgress,
    onSubmit,
    initialValues,
    appConfig,
    shouldAutofocusSearch,
    searchSuggestionProps,
  } = props;

  // Refs for search input autofocus. Both the mobile and desktop search forms
  // are always mounted (one is hidden via CSS per viewport), so we keep a ref
  // for each and focus whichever one is actually visible.
  const searchInputRef = useRef(null);
  const desktopSearchInputRef = useRef(null);

  // Autofocus search input when menu opens via search icon
  useEffect(() => {
    if (isOpen && shouldAutofocusSearch) {
      // Delay to ensure the input is rendered and focusable
      const timeoutId = setTimeout(() => {
        try {
          // Focus the visible input (getClientRects is empty for display:none).
          const input = [
            searchInputRef.current,
            desktopSearchInputRef.current,
          ].find(
            node =>
              node &&
              typeof node.focus === 'function' &&
              node.getClientRects().length > 0
          );
          if (input) {
            input.focus();
            // Also trigger click for mobile keyboards on some devices
            if (typeof input.click === 'function') {
              input.click();
            }
          }
        } catch (error) {
          console.warn('Failed to autofocus search input:', error);
        }
      }, 250);

      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, shouldAutofocusSearch]);

  // Use the total notification count from props (includes system notifications)
  const totalNotificationCount = notificationCount || 0;

  // Check if current page is a settings page to auto-expand the Settings section
  const isAccountSettingsPage = [
    'ContactDetailsPage',
    'PasswordChangePage',
    'StripePayoutPage',
    'PaymentMethodsPage',
    'ProfileSettingsPage',
    'NotificationsPage',
  ].includes(currentPage);

  const [isSettingsExpanded, setIsSettingsExpanded] = useState(
    isAccountSettingsPage
  );

  // Add or remove the `no-scroll` class on the <body> element
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add(css.noScroll); // Add the no-scroll class when the menu is open
    } else {
      document.body.classList.remove(css.noScroll); // Remove the no-scroll class when the menu is closed
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove(css.noScroll); // Ensure the class is removed when the component unmounts
    };
  }, [isOpen]);

  // Update Settings expansion when currentPage changes
  useEffect(() => {
    setIsSettingsExpanded(isAccountSettingsPage);
  }, [currentPage, isAccountSettingsPage]);

  const extraLinks = customLinks.map(linkConfig => {
    return (
      <CustomLinkComponent
        key={linkConfig.text}
        linkConfig={linkConfig}
        currentPage={currentPage}
        closeMenu={closeMenu} // Pass closeMenu to CustomLinkComponent
      />
    );
  });

  const listingCategories = categories.map(category => {
    return (
      <CategoryLinkComponent
        key={category.id}
        categories={category}
        currentPage={currentPage}
        closeMenu={closeMenu} // Pass closeMenu to CustomLinkComponent
      />
    );
  });

  const userNavLinks = isAuthenticated
    ? [
        {
          text: (
            <span>
              <FormattedMessage id="UserNav.favoriteListings" />
              {favoritesCount > 0 && ` (${favoritesCount})`}
            </span>
          ),
          route: { name: 'FavoriteListingsPage' },
        },
        {
          text: (
            <span>
              <FormattedMessage id="UserNav.savedSearches" />
              {savedSearchesCount > 0 && ` (${savedSearchesCount})`}
            </span>
          ),
          route: { name: 'SavedSearchesPage' },
        },
        {
          text: <FormattedMessage id="Your Orders" />,
          route: { name: 'InboxPage', params: { tab: 'orders' } },
          showNotificationBadge: true,
        },
        {
          text: (
            <span>
              <FormattedMessage id="UserNav.yourListings" />
              {listingsCount > 0 && ` (${listingsCount})`}
            </span>
          ),
          route: { name: 'ManageListingsPage' },
        },
      ]
    : [];

  const settingsItems = [
    {
      text: (
        <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.contactDetailsTabTitle" />
      ),
      route: { name: 'ContactDetailsPage' },
    },
    {
      text: (
        <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.passwordTabTitle" />
      ),
      route: { name: 'PasswordChangePage' },
    },
    {
      text: (
        <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentsTabTitle" />
      ),
      route: { name: 'StripePayoutPage' },
    },
    {
      text: (
        <FormattedMessage id="LayoutWrapperAccountSettingsSideNav.paymentMethodsTabTitle" />
      ),
      route: { name: 'PaymentMethodsPage' },
    },
    {
      text: <FormattedMessage id="UserNav.profileSettings" />,
      route: { name: 'ProfileSettingsPage' },
    },
  ];

  const userNavComponents = userNavLinks.map((linkConfig, index) => {
    const { text, route } = linkConfig;
    const { name, params } = route;

    const handleClick = () => {
      if (closeMenu) closeMenu();
    };

    // Check if this is the current page
    const isCurrentPage = () => {
      if (name === 'InboxPage' && params?.tab) {
        // For inbox pages, check both page name and tab
        return currentPage === 'InboxPage' && params.tab === 'orders';
      }
      return currentPage === name;
    };

    const linkClassName = classNames(css.userNavLink, {
      [css.currentPageBold]: isCurrentPage(),
    });

    return (
      <div key={index} onClick={handleClick} className={linkClassName}>
        <NamedLink name={name} params={params}>
          <span className={css.navLinkText}>
            {text}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              className={css.subLinkArrow}
            >
              <path
                d="M1 6H11M11 6L6 1M11 6L6 11"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </NamedLink>
      </div>
    );
  });

  const socialLinks = [
    {
      name: 'Instagram',
      href: 'https://www.instagram.com/used.icons/?hl=en',
    },
    {
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/company/used-icons',
    },
  ];

  const socialLinkComponents = socialLinks.map((link, index) => (
    <div key={index} onClick={closeMenu} className={css.socialLink}>
      <ExternalLink href={link.href}>{link.name}</ExternalLink>
    </div>
  ));

  const createListingButton = (
    <div onClick={() => { if (closeMenu) closeMenu(); }} className={css.createListingButton}>
      <NamedLink name="NewListingPage">Create listing</NamedLink>
    </div>
  );

  const logoutButton = isAuthenticated ? (
    <div
      onClick={() => {
        if (closeMenu) closeMenu();
        onLogout();
      }}
      className={css.logoutButton}
    >
      Log out
    </div>
  ) : null;

  return (
    <div className={classNames(css.root, { [css.hidden]: !isOpen })}>
      <div className={css.content}>
        <div className={css.contentWrapper}>
          {/* Search Bar - At top of menu on mobile only */}
          <div className={css.searchWrapperMobile}>
            <div className={css.searchContainer}>
              <TopbarSearchForm
                onSubmit={onSubmit}
                initialValues={initialValues}
                isMobile
                appConfig={appConfig}
                inputRef={element => {
                  searchInputRef.current = element;
                }}
                searchSuggestionProps={searchSuggestionProps}
              />
            </div>
          </div>

          <div className={css.columnsWrapper}>
            {/* Right Column: Account Related Items - Shows first on mobile, right side on desktop */}
            <div className={css.rightSide}>
              {!isAuthenticated ? (
                <div className={css.authFormContainerDesktop}>
                  <InlineAuthenticationForms
                    showFacebookLogin={true}
                    showGoogleLogin={true}
                    submitLogin={values => {
                      if (onLogin) {
                        return onLogin(values);
                      }
                    }}
                    loginError={loginError}
                    signupError={signupError}
                    authInProgress={authInProgress}
                    submitSignup={values => {
                      if (onSignup) {
                        return onSignup(values);
                      }
                    }}
                  />
                </div>
              ) : (
                isAuthenticated &&
                currentUser && (
                  <>
                    <div className={css.userInfoSection}>
                      <div className={css.userProfile}>
                        <div
                          className={css.avatarContainer}
                          onClick={() => {
                            if (closeMenu) closeMenu();
                          }}
                        >
                          <NamedLink name="NotificationsPage">
                            <Avatar
                              className={css.avatar}
                              user={currentUser}
                              disableProfileLink
                            />
                          </NamedLink>
                        </div>
                        <div className={css.userDetails}>
                          <div className={css.welcomeMessage}>
                            Hi, welcome back!
                          </div>
                          <div className={css.userName}>
                            {currentUser.attributes.profile.firstName}{' '}
                            {currentUser.attributes.profile.lastName}
                          </div>
                        </div>
                        <div className={css.notificationSection}>
                          <div
                            className={css.notificationIcon}
                            title={
                              totalNotificationCount > 0
                                ? `${totalNotificationCount} new notifications`
                                : 'Notifications'
                            }
                            onClick={() => {
                              if (closeMenu) closeMenu();
                            }}
                          >
                            <NamedLink name="NotificationsPage">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="40"
                                height="40"
                                viewBox="0 0 30 30"
                                fill="none"
                                className={css.bellIcon}
                              >
                                <path
                                  d="M15 0.5C23.0081 0.5 29.5 6.99187 29.5 15C29.5 23.0081 23.0081 29.5 15 29.5H0.5V15C0.5 6.99187 6.99187 0.5 15 0.5Z"
                                  stroke="currentColor"
                                  fill="none"
                                />
                              </svg>
                              {totalNotificationCount > 0 && (
                                <NotificationBadge
                                  className={css.notificationBadge}
                                  count={totalNotificationCount}
                                />
                              )}
                            </NamedLink>
                          </div>
                        </div>
                      </div>

                      <div className={css.userNavWrapper}>
                        {userNavComponents}

                        {/* Settings Section */}
                        <div className={css.settingsSection}>
                          <div
                            className={classNames(css.userNavLink, {
                              [css.currentPageBold]: isAccountSettingsPage,
                            })}
                            onClick={() =>
                              setIsSettingsExpanded(!isSettingsExpanded)
                            }
                          >
                            Settings
                            <span className={css.expandIcon}>
                              {isSettingsExpanded ? '−' : '+'}
                            </span>
                          </div>
                          {isSettingsExpanded &&
                            settingsItems.map((subItem, subIndex) => {
                              const isCurrentSettingsPage =
                                currentPage === subItem.route.name ||
                                currentPage?.endsWith(subItem.route.name);
                              const subLinkClassName = classNames(
                                css.userNavSubLink,
                                {
                                  [css.currentPageBold]: isCurrentSettingsPage,
                                }
                              );

                              return (
                                <div
                                  key={subIndex}
                                  onClick={() => closeMenu && closeMenu()}
                                  className={subLinkClassName}
                                >
                                  <NamedLink
                                    name={subItem.route.name}
                                    params={subItem.route.params}
                                  >
                                    <span
                                      className={
                                        isCurrentSettingsPage
                                          ? css.boldText
                                          : ''
                                      }
                                    >
                                      {subItem.text}
                                    </span>
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="12"
                                      height="12"
                                      viewBox="0 0 12 12"
                                      fill="none"
                                      className={css.subLinkArrow}
                                    >
                                      <path
                                        d="M1 6H11M11 6L6 1M11 6L6 11"
                                        stroke="currentColor"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </NamedLink>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                  </>
                )
              )}

              {/* Desktop Logout Button - only show on desktop for authenticated users */}
              {isAuthenticated && (
                <div className={css.desktopLogoutWrapper}>
                  <div
                    onClick={() => {
                      if (closeMenu) closeMenu();
                      onLogout();
                    }}
                    className={css.logoutButton}
                  >
                    Log out
                  </div>
                </div>
              )}
            </div>

            {/* Mobile horizontal divider between sections - only show for authenticated users */}
            {isAuthenticated && <div className={css.mobileDivider}></div>}

            {/* Left Column: General Menu Items */}
            <div className={css.leftSide}>
              {/* Search Bar - In left section on desktop only */}
              <div className={css.searchWrapperDesktop}>
                <div className={css.searchContainer}>
                  <TopbarSearchForm
                    onSubmit={onSubmit}
                    initialValues={initialValues}
                    isMobile
                    appConfig={appConfig}
                    inputRef={element => {
                      desktopSearchInputRef.current = element;
                    }}
                    searchSuggestionProps={searchSuggestionProps}
                  />
                </div>
              </div>
              <div className={css.mainContent}>
                <div className={css.leftColumn}>
                  {extraLinks}
                  {listingCategories}
                  <div
                    onClick={() => {
                      if (closeMenu) closeMenu();
                    }}
                    className={classNames(css.navigationLink, {
                      [css.currentPage]:
                        currentPage?.indexOf('EditorialsPage') === 0,
                    })}
                  >
                    <NamedLink name="EditorialsPage">
                      <span className={css.menuItemBorder} />
                      News
                    </NamedLink>
                  </div>

                  {/* Showroom appointment text - positioned at bottom of navigation links (desktop only) */}
                  <div className={css.showroomTextDesktop}>
                    Our showroom is open Sundays 11-16. We're also happy to
                    arrange a visit outside these hours — just send us an
                    email at
                    <a href="mailto:contact@usedicons.com">
                      &nbsp;contact@usedicons.com
                    </a>
                    .
                  </div>
                </div>

                <div className={css.rightColumn}>
                  <div className={css.socialLinksWrapper}>
                    {socialLinkComponents}
                  </div>
                </div>
              </div>

              {/* Mobile divider above login form */}
              {!isAuthenticated && <div className={css.mobileDivider}></div>}

              {/* Authentication form for non-authenticated users - appears at bottom on mobile */}
              {!isAuthenticated && (
                <div className={css.authFormContainerMobile}>
                  <InlineAuthenticationForms
                    showFacebookLogin={true}
                    showGoogleLogin={true}
                    submitLogin={values => {
                      if (onLogin) {
                        return onLogin(values);
                      }
                    }}
                    loginError={loginError}
                    signupError={signupError}
                    authInProgress={authInProgress}
                    submitSignup={values => {
                      if (onSignup) {
                        return onSignup(values);
                      }
                    }}
                    termsAndConditions={
                      <FormattedMessage
                        id="AuthenticationPage.termsAndConditions"
                        values={{
                          termsLink: (
                            <ExternalLink
                              href="/terms"
                              className={css.termsLink}
                            >
                              <FormattedMessage id="AuthenticationPage.termsLinkText" />
                            </ExternalLink>
                          ),
                          privacyLink: (
                            <ExternalLink
                              href="/privacy"
                              className={css.termsLink}
                            >
                              <FormattedMessage id="AuthenticationPage.privacyLinkText" />
                            </ExternalLink>
                          ),
                        }}
                      />
                    }
                  />
                </div>
              )}
            </div>
          </div>

          {/* Divider between navigation and action buttons */}
          {isAuthenticated && <div className={css.actionButtonsDivider}></div>}

          {/* Action buttons at bottom of mobile menu */}
          {isAuthenticated && (
            <div className={css.actionButtons}>
              {createListingButton}
              {logoutButton}
            </div>
          )}

          {/* Showroom appointment text - positioned at bottom of page (mobile only) */}
          <div className={css.showroomTextMobile}>
            Our showroom is open Sundays 11-16. We're also happy to arrange a
            visit outside these hours — just send us an email at
            info@usedicons.com.
          </div>
        </div>
      </div>
    </div>
  );
};

TopbarMobileMenu.defaultProps = {
  currentUser: null,
  notificationCount: 0,
  listingsCount: 0,
  favoritesCount: 0,
  savedSearchesCount: 0,
  currentPage: null,
  onLogin: null,
  onSignup: null,
  loginError: null,
  signupError: null,
  authInProgress: false,
  rootClassName: null,
  className: null,
  desktopInputRoot: null,
  isMobile: false,
  initialValues: {},
  searchSuggestionProps: {},
};

const { bool, func, number, string, object } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  listingsCount: number,
  favoritesCount: number,
  savedSearchesCount: number,
  onLogout: func.isRequired,
  onLogin: func,
  onSignup: func,
  loginError: propTypes.error,
  signupError: propTypes.error,
  authInProgress: bool,
  isOpen: PropTypes.bool.isRequired,
  categories: PropTypes.array,
  customLinks: PropTypes.array,
  closeMenu: func,
  rootClassName: string,
  className: string,
  desktopInputRoot: string,
  isMobile: bool,
  appConfig: object.isRequired,
  // Bundle of keyword-autocomplete props forwarded to TopbarSearchForm.
  searchSuggestionProps: object,
};

export default TopbarMobileMenu;
